import { useContext, useEffect, useState, type ReactNode } from "react"
import { useSearchParams } from "react-router"
import Grid from "@mui/material/Grid2"
import { Button, Chip, IconButton, Tooltip } from "@mui/material"
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined'
import * as SunCalc from "suncalc"
import { rootContext } from "../../contexts/rootContext"
import { estimateTripDurationLabel, estimateTripDurationMinutes, formatMinutesLabel, getAccessPointCoord, getAverageFloatabilityForSegment, getDistanceBetweenAccessPoints } from "../../utilities/tripHelper"
import { getFloatabilityCategory } from "../../utilities/buffaloArcGisHelper"
import { getContrastingTextColor, toLegibleOnBlack } from "../../utilities/colorHelper"
import { getProjectedAccessPointCoord, getProjectedRiverSegmentPoints, getZoomedSegmentView, riverPathSvgPoints, riverSvgViewBox } from "../../utilities/riverPathProjection"
import { fromDateInputValue, getDateRelation, getQuickPickDates, getUpcomingWeekendDate, formatDayLabel, isSameDate, toDateInputValue } from "../../utilities/dateHelper"
import { PrimitiveAccessWarning } from "./PrimitiveAccessWarning"
import { TripPointsOfInterest } from "./TripPointsOfInterest"

const ASSUMED_PADDLING_HOURS_PER_DAY = 8

interface TripDetailsProps {
    accessPoint1: string
    accessPoint2: string
    latestPolylineFloatability: number[]
    latestReadingTime?: string
    onBack: () => void
}

const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })

interface DirectionsRowProps {
    label: string
    value: ReactNode
    href: string
    icon: ReactNode
}

// Access point names are long enough to push the directions link onto its own line on
// mobile, so the label column is the only part allowed to shrink and wrap — the link
// keeps its full width and stays on the row.
const DirectionsRow = (props: DirectionsRowProps) => {
    const context = useContext(rootContext)

    return (
        <Grid
            container
            size={12}
            spacing={1.5}
            sx={{
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "nowrap",
                backgroundColor: "#1a1a1a",
                borderRadius: "10px",
                padding: "10px 15px"
            }}
        >
            <Grid size="grow" sx={{ minWidth: 0 }}>
                <div style={{ color: context.theme.text.contrast5, fontSize: "12px" }}>
                    {props.label}
                </div>
                <div style={{ overflowWrap: "anywhere" }}>{props.value}</div>
            </Grid>
            <Grid sx={{ flexShrink: 0 }}>
                <a
                    href={props.href}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                        color: context.theme.text.contrast4,
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                        textDecoration: "none",
                        cursor: `url(${context.theme.cursor.pointer}), pointer`
                    }}
                >
                    {props.icon}
                    Directions
                </a>
            </Grid>
        </Grid>
    )
}

export const TripDetails = (props: TripDetailsProps) => {
    const context = useContext(rootContext)
    const [distance, setDistance] = useState<string>()
    const [duration, setDuration] = useState<string>()
    const [shareLabel, setShareLabel] = useState("Share")
    const [searchParams, setSearchParams] = useSearchParams()

    const dateParam = searchParams.get("date")
    const selectedDate = dateParam !== null ? fromDateInputValue(dateParam) : getUpcomingWeekendDate()
    // River level forecasting isn't built yet, so sensor-derived levels only describe
    // right now — any other day gets an explicit "no forecast" treatment instead of
    // today's number dressed up as a prediction.
    const dateRelation = getDateRelation(selectedDate)
    const isPlanningForToday = dateRelation === "today"

    const handleSelectDate = (date: Date) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.set("date", toDateInputValue(date))
            return next
        }, { replace: true })
    }

    useEffect(() => {
        const runAndSetDistance = async () => {
            const distance = await getDistanceBetweenAccessPoints(props.accessPoint1, props.accessPoint2)
            setDistance(distance)
            setDuration(estimateTripDurationLabel(parseFloat(distance)))
        }
        runAndSetDistance()
    }, [props.accessPoint1, props.accessPoint2])

    // Freezes an explicit date into the URL as soon as the page is opened, so a
    // shared link always reflects the exact day being viewed, not "whatever the
    // recipient's default resolves to" if the query string never gets `date` set.
    useEffect(() => {
        if (searchParams.get("date") === null)
            handleSelectDate(getUpcomingWeekendDate())
    }, [])

    const floatabilityScore = getAverageFloatabilityForSegment(props.latestPolylineFloatability, props.accessPoint1, props.accessPoint2)
    const floatabilityCategory = floatabilityScore !== null ? getFloatabilityCategory(floatabilityScore) : null

    const segmentSvgPoints = getProjectedRiverSegmentPoints(props.accessPoint1, props.accessPoint2)
    const [accessPoint1X, accessPoint1Y] = getProjectedAccessPointCoord(props.accessPoint1)
    const [accessPoint2X, accessPoint2Y] = getProjectedAccessPointCoord(props.accessPoint2)
    const zoomedSegment = getZoomedSegmentView(props.accessPoint1, props.accessPoint2)

    const putInCoord = getAccessPointCoord(props.accessPoint1)
    const takeOutCoord = getAccessPointCoord(props.accessPoint2)
    const sunTimes = SunCalc.getTimes(selectedDate, putInCoord.latitude, putInCoord.longitude)
    const dateOptions = getQuickPickDates()

    const durationMinutes = distance !== undefined ? estimateTripDurationMinutes(parseFloat(distance)) : null
    const daylightMinutes = sunTimes.sunrise !== null && sunTimes.sunset !== null
        ? (sunTimes.sunset.getTime() - sunTimes.sunrise.getTime()) / (60 * 1000)
        : null
    const fitsInSingleDay = durationMinutes !== null && daylightMinutes !== null && durationMinutes <= daylightMinutes
    const latestLaunchTime = fitsInSingleDay && durationMinutes !== null && sunTimes.sunset !== null
        ? new Date(sunTimes.sunset.getTime() - durationMinutes * 60 * 1000)
        : null
    const estimatedTripDays = durationMinutes !== null
        ? Math.max(1, Math.ceil(durationMinutes / (ASSUMED_PADDLING_HOURS_PER_DAY * 60)))
        : null

    const selectedDayLabel = formatDayLabel(selectedDate)
    const forecastTitle = dateRelation === "future"
        ? `No water level forecast for ${selectedDayLabel}`
        : `No water level history for ${selectedDayLabel}`
    const forecastNote = dateRelation === "future"
        ? `Forecasting the river level for a future date is a planned feature, but isn't built yet.`
        : `Water levels for past dates is not currently available.`

    const shuttleDirectionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${takeOutCoord.latitude},${takeOutCoord.longitude}&destination=${putInCoord.latitude},${putInCoord.longitude}`

    const handleShare = async () => {
        const shareData = {
            title: "Buffalo River Trip",
            text: `${props.accessPoint1} to ${props.accessPoint2} on the Buffalo River — planned for ${formatDayLabel(selectedDate)}`,
            url: window.location.href
        }

        if (navigator.share) {
            try {
                await navigator.share(shareData)
            } catch {
                // user cancelled the share sheet
            }
            return
        }

        try {
            await navigator.clipboard.writeText(window.location.href)
            setShareLabel("Link Copied!")
            setTimeout(() => setShareLabel("Share"), 2000)
        } catch {
            // clipboard unavailable
        }
    }

    const getDirectionsUrl = (accessPointName: string) => {
        const coord = getAccessPointCoord(accessPointName)
        return `https://www.google.com/maps/dir/?api=1&destination=${coord.latitude},${coord.longitude}`
    }

    return (
        <Grid
            container
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "black",
                color: "white",
                zIndex: 1200,
                overflowY: "auto",
                userSelect: "none"
            }}
        >
            <Grid
                container
                size={12}
                sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "black",
                    zIndex: 1,
                    borderBottom: "1px solid rgba(255,255,255,0.1)"
                }}
            >
                <Grid
                    container
                    size={12}
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: { xs: "10px 20px", sm: "10px 60px" },
                        maxWidth: "700px",
                        width: "100%",
                        margin: "0 auto"
                    }}
                >
                    <Grid sx={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title="Back to Map">
                            <IconButton onClick={props.onBack} sx={{ color: context.theme.text.contrast4 }} aria-label="back to map">
                                <ArrowBackOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                        <span style={{ fontSize: "20px", marginLeft: "10px" }}>Trip Details</span>
                    </Grid>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ShareOutlinedIcon />}
                        onClick={handleShare}
                        sx={{
                            backgroundColor: context.theme.text.contrast4,
                            color: "black",
                            cursor: `url(${context.theme.cursor.pointer}), pointer`,
                            "&:hover": { backgroundColor: context.theme.text.contrast4, opacity: 0.85 }
                        }}
                    >
                        {shareLabel}
                    </Button>
                </Grid>
            </Grid>
            <Grid
                container
                size={12}
                spacing={3}
                sx={{
                    padding: { xs: "10px 20px 30px", sm: "10px 60px 30px" },
                    maxWidth: "700px",
                    margin: "0 auto"
                }}
            >
                <Grid size={12} sx={{ textAlign: "center", fontSize: "28px" }}>
                    {props.accessPoint1} <span style={{ color: context.theme.text.contrast5 }}>&rarr;</span> {props.accessPoint2}
                </Grid>

                <PrimitiveAccessWarning accessPointNames={[props.accessPoint1, props.accessPoint2]} />

                <Grid size={12} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <span style={{ color: context.theme.text.contrast5, fontSize: "13px" }}>Planning for:</span>
                    {dateOptions.map(option => (
                        <Chip
                            key={option.label}
                            label={option.label}
                            size="small"
                            onClick={() => handleSelectDate(option.date)}
                            sx={isSameDate(option.date, selectedDate) ? {
                                cursor: `url(${context.theme.cursor.pointer}), pointer`,
                                backgroundColor: context.theme.text.contrast4,
                                color: getContrastingTextColor(context.theme.text.contrast4),
                                border: `1px solid ${context.theme.text.contrast4}`,
                                fontWeight: "bold",
                                "&:hover": { backgroundColor: context.theme.text.contrast4, opacity: 0.85 }
                            } : {
                                cursor: `url(${context.theme.cursor.pointer}), pointer`,
                                // A filled dark chip barely separates from the black panel, so the
                                // unselected state is outlined instead — the border carries the shape
                                // and the accent fill is left to mean "selected".
                                backgroundColor: "transparent",
                                color: "rgba(255,255,255,0.85)",
                                border: "1px solid rgba(255,255,255,0.4)",
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.08)",
                                    borderColor: "rgba(255,255,255,0.7)",
                                    color: "white"
                                }
                            }}
                        />
                    ))}
                    <input
                        type="date"
                        value={toDateInputValue(selectedDate)}
                        onChange={(event) => {
                            if (event.target.value)
                                handleSelectDate(fromDateInputValue(event.target.value))
                        }}
                        style={{
                            colorScheme: "dark",
                            backgroundColor: "#333",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "4px 10px",
                            fontFamily: "inherit",
                            fontSize: "13px",
                            cursor: `url(${context.theme.cursor.pointer}), pointer`
                        }}
                    />
                </Grid>

                <Grid
                    size={12}
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "16px"
                    }}
                >
                    <svg
                        viewBox={`0 0 ${zoomedSegment.viewBoxWidth} ${zoomedSegment.viewBoxHeight}`}
                        style={{ width: "100%", maxWidth: "380px" }}
                        role="img"
                        aria-label={`Zoomed-in map of the Buffalo River stretch from ${props.accessPoint1} to ${props.accessPoint2}`}
                    >
                        <polyline
                            points={zoomedSegment.pathPoints}
                            fill="none"
                            stroke={context.theme.text.contrast4}
                            strokeWidth={4}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <circle cx={zoomedSegment.accessPoint1[0]} cy={zoomedSegment.accessPoint1[1]} r={6} fill={context.theme.text.contrast5} stroke="black" strokeWidth={1.5} />
                        <text x={zoomedSegment.accessPoint1[0]} y={zoomedSegment.accessPoint1[1] - 12} textAnchor="middle" fontSize="13" fill="white">Put-In</text>
                        <circle cx={zoomedSegment.accessPoint2[0]} cy={zoomedSegment.accessPoint2[1]} r={6} fill={context.theme.text.contrast5} stroke="black" strokeWidth={1.5} />
                        <text x={zoomedSegment.accessPoint2[0]} y={zoomedSegment.accessPoint2[1] - 12} textAnchor="middle" fontSize="13" fill="white">Take-Out</text>
                    </svg>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                        <svg
                            viewBox={`0 0 ${riverSvgViewBox.width} ${riverSvgViewBox.height}`}
                            style={{ width: "140px" }}
                            role="img"
                            aria-label={`Map of the full Buffalo River with the stretch from ${props.accessPoint1} to ${props.accessPoint2} highlighted`}
                        >
                            <polyline
                                points={riverPathSvgPoints}
                                fill="none"
                                stroke="white"
                                strokeOpacity={0.25}
                                strokeWidth={1.5}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <polyline
                                points={segmentSvgPoints}
                                fill="none"
                                stroke={context.theme.text.contrast4}
                                strokeWidth={4}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <circle cx={accessPoint1X} cy={accessPoint1Y} r={5} fill={context.theme.text.contrast5} stroke="black" strokeWidth={1.5} />
                            <circle cx={accessPoint2X} cy={accessPoint2Y} r={5} fill={context.theme.text.contrast5} stroke="black" strokeWidth={1.5} />
                        </svg>
                        <span style={{ fontSize: "11px", color: context.theme.text.contrast5 }}>Location on the full river</span>
                    </div>
                </Grid>

                <Grid container size={12} spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: "center" }}>
                        <div style={{ color: context.theme.text.contrast5 }}>Distance</div>
                        <div style={{ fontSize: "20px" }}>{distance ?? "..."} miles</div>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: "center" }}>
                        <div style={{ color: context.theme.text.contrast5 }}>Estimated Time</div>
                        <div style={{ fontSize: "20px" }}>{duration ?? "..."}</div>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: "center" }}>
                        <div style={{ color: context.theme.text.contrast5 }}>
                            {isPlanningForToday ? "Estimated Level" : "Water Level"}
                        </div>
                        {isPlanningForToday ?
                            <div style={{ fontSize: "20px", color: floatabilityCategory !== null ? toLegibleOnBlack(floatabilityCategory.color) : "white" }}>
                                {floatabilityCategory?.label ?? "N/A"}
                            </div> :
                            <div style={{ fontSize: "20px", color: "rgba(255,255,255,0.4)" }}>
                                {dateRelation === "future" ? "No Forecast" : "Unavailable"}
                            </div>
                        }
                    </Grid>
                </Grid>

                <Grid size={12} sx={{ fontSize: "13px", color: context.theme.text.contrast2, textAlign: "center" }}>
                    Estimated time is based on an average paddling speed of 2.5mph. Water level is estimated from nearby USGS sensors and only describes conditions right now. Actual conditions will vary — always verify current conditions before your trip.
                </Grid>

                {isPlanningForToday ?
                    floatabilityCategory !== null &&
                        <Grid
                            size={12}
                            sx={{
                                backgroundColor: "#1a1a1a",
                                borderLeft: `4px solid ${floatabilityCategory.color}`,
                                borderRadius: "4px",
                                padding: "10px 15px"
                            }}
                        >
                            {floatabilityCategory.guidance}
                            {props.latestReadingTime &&
                                <div style={{ color: context.theme.text.contrast2, fontSize: "12px", marginTop: "6px" }}>
                                    Based on current sensor readings from {props.latestReadingTime}
                                </div>
                            }
                        </Grid> :
                    <Grid
                        container
                        size={12}
                        spacing={1}
                        sx={{
                            backgroundColor: "#1a1a1a",
                            borderLeft: `4px solid ${context.theme.text.contrast2}`,
                            borderRadius: "4px",
                            padding: "10px 15px",
                            alignItems: "flex-start",
                            flexWrap: "nowrap"
                        }}
                    >
                        <Grid>
                            <UpdateOutlinedIcon sx={{ color: context.theme.text.contrast2 }} />
                        </Grid>
                        <Grid size="grow">
                            <div style={{ color: context.theme.text.contrast2 }}>{forecastTitle}</div>
                            <div style={{ fontSize: "13px", marginTop: "4px" }}>{forecastNote}</div>
                            {floatabilityCategory !== null &&
                                <div style={{ color: context.theme.text.contrast2, fontSize: "12px", marginTop: "6px" }}>
                                    Right now the river is running <span style={{ color: toLegibleOnBlack(floatabilityCategory.color) }}>{floatabilityCategory.label}</span>
                                    {props.latestReadingTime !== undefined && ` as of ${props.latestReadingTime}`}. Check back closer to your trip for live conditions.
                                </div>
                            }
                        </Grid>
                    </Grid>
                }

                <Grid
                    container
                    size={12}
                    sx={{
                        backgroundColor: "#1a1a1a",
                        borderRadius: "10px",
                        padding: "10px 15px"
                    }}
                    spacing={1.5}
                >
                    <Grid container size={12} sx={{ alignItems: "center" }} spacing={1}>
                        <Grid>
                            {fitsInSingleDay ?
                                <WbSunnyOutlinedIcon sx={{ color: context.theme.text.contrast2 }} /> :
                                <WarningAmberOutlinedIcon sx={{ color: context.theme.text.contrast1 }} />
                            }
                        </Grid>
                        <Grid size="grow">
                            <div style={{ color: context.theme.text.contrast5, fontSize: "12px" }}>
                                Daylight — {formatDayLabel(selectedDate)}
                            </div>
                            <div>
                                {sunTimes.sunrise !== null && sunTimes.sunset !== null ?
                                    `${formatTime(sunTimes.sunrise)} - ${formatTime(sunTimes.sunset)}` :
                                    "N/A"
                                }
                            </div>
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        {durationMinutes === null ? null :
                            fitsInSingleDay && latestLaunchTime !== null ?
                                <span>Launch by <span style={{ color: context.theme.text.contrast3 }}>{formatTime(latestLaunchTime)}</span> to reach the take-out before sunset.</span> :
                                daylightMinutes !== null &&
                                    <span style={{ color: context.theme.text.contrast1 }}>
                                        This stretch takes longer than one day of daylight ({formatMinutesLabel(daylightMinutes)} on {formatDayLabel(selectedDate)}).
                                        At roughly {ASSUMED_PADDLING_HOURS_PER_DAY} hours of paddling per day, plan for about {estimatedTripDays} {estimatedTripDays === 1 ? "day" : "days"} on the river.
                                    </span>
                        }
                    </Grid>
                </Grid>

                <TripPointsOfInterest accessPoint1={props.accessPoint1} accessPoint2={props.accessPoint2} />

                <Grid size={12} sx={{ fontWeight: "bold", fontSize: "18px", color: context.theme.text.contrast5 }}>
                    Directions
                </Grid>
                {[props.accessPoint1, props.accessPoint2].map((accessPointName, index) => (
                    <DirectionsRow
                        key={accessPointName}
                        label={index === 0 ? "Put-In" : "Take-Out"}
                        value={accessPointName}
                        href={getDirectionsUrl(accessPointName)}
                        icon={<DirectionsOutlinedIcon sx={{ marginRight: "4px" }} />}
                    />
                ))}
                <DirectionsRow
                    label="Shuttle"
                    value={<>Take-Out &rarr; Put-In</>}
                    href={shuttleDirectionsUrl}
                    icon={<DirectionsCarOutlinedIcon sx={{ marginRight: "4px" }} />}
                />
                <Grid size={12} sx={{ fontSize: "12px", color: context.theme.text.contrast2, marginTop: "-12px" }}>
                    Drop a car at the take-out before driving to the put-in, so you have a ride back at the end.
                </Grid>
            </Grid>
        </Grid>
    )
}
